import React, { FC, ReactNode, useMemo } from 'react';
import InfiniteScroll from 'react-infinite-scroll-component';
import { useTranslation } from 'react-i18next';
import EmptyPlaceholder from '../emptyPlaceholder/emptyPlaceholder';
import { DateUtils } from '@jaam/commons';
import { ListLoader } from '../ui/listLoader';

type OrderBy = 'year' | 'month' | 'day';

interface ItemComponentProps {
  data: any;
  onSelect?: Props['onSelect'];
  itemClass?: string;
  selectedId?: Props['selectedId'];
  color?: string;
}

interface Props<ExtraProps = Record<string, unknown>> {
  data: any[];
  hasMore?: boolean;
  useGrouping?: boolean;
  groupHaveBackground?: boolean;
  groupDataBy?: (
    data: any[],
    dateField: string,
    orderBy: OrderBy,
  ) => { [key: string]: any[] };
  isLoading: boolean;
  isError: boolean;
  Loader?: ReactNode;
  ItemComponent: React.ComponentType<ItemComponentProps & ExtraProps>;
  onSelect?: (
    e: React.MouseEvent<HTMLLIElement> | string | number,
    color?: string,
    label?: string,
    position?: string,
    other?: any,
  ) => void;
  loaderRef?: React.RefObject<HTMLDivElement>;
  customMessage?: string;
  listClassName?: string;
  ulClassName?: string;
  subUlClassName?: string;
  stickyHeight?: number;
  itemClass?: string;
  selectedId?: number | string | null;
  subTitle?: ReactNode;
  fetchMoreData?: () => void;
  itemProps?: ExtraProps;
  dateField?: string;
  orderBy?: OrderBy;
  color?: string;
  emptyPlaceholder?: string;
}

export const ListCard: FC<Props> = ({
  data,
  hasMore,
  useGrouping = false,
  groupDataBy,
  groupHaveBackground = true,
  isLoading,
  isError,
  Loader,
  onSelect,
  subUlClassName,
  color,
  customMessage,
  listClassName,
  stickyHeight,
  itemClass,
  selectedId,
  subTitle,
  fetchMoreData,
  ulClassName,
  ItemComponent,
  itemProps = {},
  dateField,
  orderBy,
  emptyPlaceholder,
}) => {
  const { t } = useTranslation();

  // Memoizing the function to prevent unnecessary re-renders
  const createFetchMoreDataFunction = useMemo(() => {
    return () => {
      if (fetchMoreData) {
        fetchMoreData();
      }
    };
  }, [fetchMoreData]);

  // Memoize grouped data to avoid re-sorting on each render
  const sortedGroupedData = useMemo(() => {
    if (!useGrouping || !groupDataBy) return null;

    const field = dateField || 'date';
    const order: OrderBy = orderBy || 'day';

    const groupedData = groupDataBy(data, field, order);
    return Object.entries(groupedData).sort((a, b) => {
      const parseDate = (value: string): number => {
        switch (order) {
          case 'year':
            return DateUtils(value, 'YYYY', true).valueOf();
          case 'month':
            return DateUtils(value, 'MMMM YYYY', true).valueOf();
          case 'day':
          default:
            return DateUtils(value, undefined, true).valueOf();
        }
      };

      const dateA = parseDate(a[0]);
      const dateB = parseDate(b[0]);

      return dateB - dateA; // Sort descending
    });
  }, [data, dateField, groupDataBy, orderBy, useGrouping]);

  const renderContent = () => {
    if (data.length === 0) {
      return (
        <EmptyPlaceholder
          customMessage={emptyPlaceholder ?? t('emptyContentMessage')}
          imageSrc="assets/svg/empty-list.svg" // mettre le message par defaut aucun enregistrement trouvé
        />
      );
    } else if (useGrouping && sortedGroupedData) {
      return renderGroupedData();
    } else {
      return renderDataList();
    }
  };

  const renderDataList = () => (
    <>
      {subTitle}
      <ul className={ulClassName || ''}>
        {data.map((item) => {
          return (
            <ItemComponent
              key={item.id}
              data={item}
              onSelect={onSelect}
              itemClass={itemClass}
              selectedId={selectedId}
              {...itemProps}
              color={color}
            />
          );
        })}
      </ul>
    </>
  );

  const renderLoading = () => <ListLoader />;

  const renderError = () => (
    <EmptyPlaceholder
      customMessage={t('errorListPlaceholder')}
      imageSrc="/assets/svg/error.svg"
    />
  );

  const renderGroupedData = () => {
    return (
      <ul className={`pb-[100px] md:pb-0 space-y-4  ${ulClassName || ''}`}>
        {sortedGroupedData!.map(([groupKey, items]) => (
          <li key={groupKey}>
            <h4
              style={{ top: stickyHeight ? stickyHeight + 4 : -36 }}
              className={` sticky  font-semibold text-xs text-neutral-12 pb-1  mb-2 capitalize z-[8] ${
                groupHaveBackground ? 'bg-neutral-98' : ''
              }`}
            >
              {groupKey}
            </h4>
            <ul
              className={`mb-4 bg-white rounded-2xl sm:mb-0 ${
                subUlClassName || ''
              }`}
            >
              {items.map((item) => (
                <ItemComponent
                  key={item.id}
                  data={item}
                  onSelect={onSelect}
                  selectedId={selectedId}
                  itemClass={itemClass}
                  {...itemProps}
                />
              ))}
            </ul>
          </li>
        ))}
      </ul>
    );
  };

  const determineContent = (): JSX.Element => {
    if (isLoading) {
      return renderLoading();
    } else if (isError) {
      return renderError();
    } else {
      return renderContent();
    }
  };

  return (
    <InfiniteScroll
      scrollableTarget="scrollableContainer"
      className={listClassName}
      dataLength={data?.length || 0}
      next={createFetchMoreDataFunction}
      hasMore={hasMore || false}
      scrollThreshold={0.5}
      style={{
        WebkitOverflowScrolling: 'touch', // Active un défilement fluide sur iOS
      }}
      loader={
        fetchMoreData && (
          <div className={`visible mt-2  relative`}>
            <div className="absolute top-0 bottom-0 bg-blue-00 w-[25%] animate-borealisBar h-0.5 "></div>
          </div>
        )
      }
    >
      {determineContent()}
    </InfiniteScroll>
  );
};
